import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Button, Icon, Input, Popconfirm, Select, Switch, Tag, Tooltip, message } from 'antd';

import {
  say, Flow, Block, BlockHead, Step, Title, Options, Or, Chip, AnyChip, Connector, Joint, JointNote, Sentence, TONES, Footer, Toolbar, PAGE, Panel, PanelHead, Row, Who, EmptyPanel, ChoiceBlock, Joiner,
} from '~/_domains/tasks/ruleUi';
import { request } from '~/_shared/instance';
import { fetch as fetchCustomerTasks } from '~/_domains/tasks/actions';
import {
  REGIMES, ACTIVITIES, PAYROLL, explainRule, getCustomerProfile, getProfile, suggestProfile,
  REACH_ENROLLED, REACH_MISSING, REACH_IN, REACH_UNKNOWN, REACH_OUTSIDE, NO_RULE_HELP,
} from '~/_domains/tasks/enrollment';

// ---------------------------------------------------------------------------
// "Quem recebe esta tarefa" — a regra que guia a sugestão de matrícula. Nada é
// matriculado sozinho: a regra sugere e a pessoa confirma.
//
// Desenhado como blocos ligados por conectores: dentro de um bloco as opções
// se somam com "ou"; entre blocos, o conector diz "e". O último bloco é o
// resultado — quem a regra pega hoje — e a lista dele tem as ações que
// resolvem cada situação, sem precisar ir cliente por cliente.
// ---------------------------------------------------------------------------


// Etiquetas e ação à direita da linha: quebram de linha antes de espremer o
// nome do cliente.
const RowSide = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex: 0 1 auto;
  max-width: 62%;

  .ant-tag { margin-right: 0; }
`;

const STATUS = {};
STATUS[REACH_ENROLLED] = { label: 'Já matriculado', color: 'green' };
STATUS[REACH_MISSING] = { label: 'Falta matricular', color: 'orange' };
STATUS[REACH_IN] = { label: 'Sugerido pela regra', color: 'green' };
STATUS[REACH_UNKNOWN] = { label: 'Sem regra', color: null };
STATUS[REACH_OUTSIDE] = { label: 'Fora da regra', color: 'red' };

const EnrollmentRule = ({ taskId, regimes, activities, payroll, onRegimes, onActivities, onPayroll, reach, none, onNone, part }) => {
  const dispatch = useDispatch();
  const [filter, setFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const panelRef = useRef(null);

  // Na coluna de ação, a lista dos clientes que a regra toca já vem aberta:
  // é ali que se age. Abre uma vez só; fechar fica fechado.
  const autoOpened = useRef(false);
  const quietScroll = useRef(false);
  useEffect(() => {
    if (part === 'result' && reach && regimes.length > 0 && !none && !autoOpened.current) {
      autoOpened.current = true;
      quietScroll.current = true;
      setFilter('all');
    }
  }, [part, reach, regimes.length, none]);

  // Abrir uma situação rola a gaveta até a lista, que aparece logo abaixo.
  useEffect(() => {
    setSearch('');
    setLimit(PAGE);
    if (quietScroll.current && filter) {
      quietScroll.current = false;
      return;
    }
    if (filter && panelRef.current && panelRef.current.scrollIntoView) {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [filter]);

  const openFilter = (key) => setFilter(filter === key ? null : key);
  const [busy, setBusy] = useState(null);

  const noRegime = regimes.length === 0;
  // Doméstica é perfil único: atividade e folha não se aplicam a ela.
  const onlyDomestic = !noRegime && regimes.every((regime) => regime === 'Doméstica');
  const cutsDisabled = noRegime || onlyDomestic;
  const rule = { regimes, activities, payroll };

  const rows = reach ? reach.rows : [];
  const byStatus = (status) => rows.filter((row) => row.status === status);
  const missing = byStatus(REACH_MISSING);
  const outside = byStatus(REACH_OUTSIDE);
  const unknownItems = byStatus(REACH_UNKNOWN)
    .map((row) => ({ row, suggestion: suggestProfile(row.customer, reach.payrollOf ? reach.payrollOf(row.customer) : undefined) }));
  const suggestions = unknownItems.filter((item) => item.suggestion && item.suggestion.key);
  const suggestionOf = (customer) => {
    const found = unknownItems.filter((item) => item.row.customer.id === customer.id);
    return found.length > 0 ? found[0].suggestion : null;
  };

  // ── ações ────────────────────────────────────────────────────────────────

  const run = async (key, customers, perCustomer, done) => {
    setBusy(key);
    let ok = 0;
    try {
      for (let i = 0; i < customers.length; i++) {
        await perCustomer(customers[i]);
        ok++;
      }
      message.success(done(ok));
    } catch (error) {
      if (ok > 0) {
        message.warning(done(ok) + ' O restante falhou — tente de novo.');
      }
    } finally {
      dispatch(fetchCustomerTasks({ showLoading: false }));
      setBusy(null);
    }
  };

  const enroll = (customers) => run(
    'enroll',
    customers,
    (customer) => request('POST', '/customer/' + customer.id + '/tasks', { task_ids: [taskId] }, false),
    (n) => say(n, '1 cliente matriculado.', n + ' clientes matriculados.')
  );

  const unenroll = (customers) => run(
    'unenroll',
    customers,
    (customer) => request('DELETE', '/customer/' + customer.id + '/tasks', { task_ids: [taskId] }, false),
    (n) => say(n, '1 cliente removido da tarefa.', n + ' clientes removidos da tarefa.')
  );

  const applyProfiles = (items) => run(
    'profile',
    items,
    (item) => request('POST', '/customer/' + item.row.customer.id + '/enrollment-profile', { profile: item.suggestion.key }, false)
      .then(() => dispatch({
        type: 'SET_CUSTOMER_ENROLLMENT_PROFILE',
        payload: { id: item.row.customer.id, profile: item.suggestion.key },
      })),
    (n) => say(n, 'Regra gravada em 1 cliente.', 'Regra gravada em ' + n + ' clientes.')
  ).then(() => setFilter(REACH_MISSING));

  const canAct = !!taskId;
  const needsSave = 'Salve a tarefa antes de matricular clientes.';

  // A linha diz duas coisas, sempre: se a regra confere e se o cliente já
  // está na tarefa — matriculado não quer dizer que a regra confira.
  const ruleTag = (row) => {
    if (row.status === REACH_OUTSIDE) return <Tag color="red" data-rule-match="no">Regra não confere</Tag>;
    if (row.status === REACH_UNKNOWN) return <Tag data-rule-match="unknown">Cliente sem regra</Tag>;
    return <Tag color="green" data-rule-match="yes">Regra confere</Tag>;
  };

  const actionFor = (row) => {
    if (row.status === REACH_ENROLLED) {
      return (
        <Popconfirm
          title="A regra sugere este cliente. Remover da tarefa mesmo assim?"
          okText="Remover" okType="danger" cancelText="Cancelar"
          onConfirm={() => unenroll([row.customer])}
          disabled={!canAct || !!busy}
        >
          <Button size="small" type="danger" ghost disabled={!canAct || !!busy} data-remove={row.customer.id}>
            Remover
          </Button>
        </Popconfirm>
      );
    }
    if (row.status === REACH_MISSING) {
      return (
        <Button size="small" type="primary" ghost disabled={!canAct || !!busy} onClick={() => enroll([row.customer])}>
          Matricular
        </Button>
      );
    }
    if (row.status === REACH_OUTSIDE) {
      return (
        <Button size="small" type="danger" ghost disabled={!canAct || !!busy} onClick={() => unenroll([row.customer])}>
          Remover
        </Button>
      );
    }
    if (row.status === REACH_UNKNOWN) {
      const suggestion = suggestionOf(row.customer);
      if (suggestion && suggestion.key) {
        return (
          <Button size="small" disabled={!!busy} onClick={() => applyProfiles([{ row, suggestion }])}>
            Aplicar sugerida
          </Button>
        );
      }
      // Sem CNAE o cadastro não diz a atividade: a pessoa escolhe entre os
      // perfis que sobraram (folha já vem do cadastro).
      const candidates = suggestion && suggestion.candidates ? suggestion.candidates : [];
      if (candidates.length === 0) {
        return <span style={{ fontSize: 12, color: '#bfbfbf' }}>sem regime no cadastro</span>;
      }
      return (
        <Select
          size="small"
          placeholder="Escolher regra"
          disabled={!!busy}
          style={{ width: 230 }}
          dropdownStyle={{ zIndex: 1200 }}
          onChange={(key) => applyProfiles([{ row, suggestion: { key } }])}
        >
          {candidates.map((key) => (
            <Select.Option key={key} value={key}>{(getProfile(key) || {}).label || key}</Select.Option>
          ))}
        </Select>
      );
    }
    return null;
  };

  const bulkAction = () => {
    if (filter === REACH_MISSING && missing.length > 0) {
      return (
        <Tooltip title={canAct ? '' : needsSave}>
          <Button size="small" type="primary" icon="user-add" loading={busy === 'enroll'} disabled={!canAct || (!!busy && busy !== 'enroll')}
            onClick={() => enroll(missing.map((row) => row.customer))}>
            Matricular {say(missing.length, 'o cliente', 'os ' + missing.length + ' clientes')}
          </Button>
        </Tooltip>
      );
    }
    if (filter === REACH_OUTSIDE && outside.length > 0) {
      return (
        <Tooltip title={canAct ? '' : needsSave}>
          <Button size="small" type="danger" icon="user-delete" loading={busy === 'unenroll'} disabled={!canAct || (!!busy && busy !== 'unenroll')}
            onClick={() => unenroll(outside.map((row) => row.customer))}>
            Remover {say(outside.length, 'o cliente', 'os ' + outside.length + ' clientes')} da tarefa
          </Button>
        </Tooltip>
      );
    }
    if (filter === REACH_UNKNOWN && suggestions.length > 0) {
      return (
        <Button size="small" type="primary" icon="thunderbolt" loading={busy === 'profile'} disabled={!!busy && busy !== 'profile'}
          onClick={() => applyProfiles(suggestions)}>
          Aplicar {say(suggestions.length, 'a regra sugerida', 'as ' + suggestions.length + ' regras sugeridas')}
        </Button>
      );
    }
    return null;
  };

  const filterHelp = {
    all: 'Situação de cada cliente diante da regra.',
    in: 'A regra sugere a tarefa para estes clientes.',
  };
  filterHelp[REACH_MISSING] = 'A regra sugere, mas o cliente ainda não está na tarefa. Confira e matricule.';
  filterHelp[REACH_ENROLLED] = 'Já estão na tarefa.';
  filterHelp[REACH_UNKNOWN] = 'Regime certo, mas o cliente ainda não tem regra (atividade e folha) para decidir. A sugestão vem da tarefa FOLHA DE PAGAMENTO e do cadastro (funcionários e CNAEs).';
  filterHelp[REACH_OUTSIDE] = 'Estão na tarefa, mas a regra não os sugere.';

  const TITLES = {
    all: 'Todos os clientes que a regra toca',
    in: 'Sugeridos pela regra',
  };
  TITLES[REACH_MISSING] = 'Faltam matricular';
  TITLES[REACH_ENROLLED] = 'Já matriculados';
  TITLES[REACH_UNKNOWN] = 'Clientes sem regra';
  TITLES[REACH_OUTSIDE] = 'Matriculados fora da regra';

  const EMPTY = {};
  EMPTY[REACH_MISSING] = 'Todo mundo que a regra pega já está na tarefa.';
  EMPTY[REACH_OUTSIDE] = 'Ninguém fora da regra.';
  EMPTY[REACH_UNKNOWN] = 'Todos os clientes do regime já têm regra.';

  const term = search.trim().toLowerCase();
  const filtered = rows.filter((row) => {
    if (filter === 'in') {
      if (!(row.status === REACH_IN || row.status === REACH_ENROLLED || row.status === REACH_MISSING)) {
        return false;
      }
    } else if (filter !== 'all' && row.status !== filter) {
      return false;
    }
    if (!term) {
      return true;
    }
    return (String(row.customer.name || '') + ' ' + String(row.customer.cod || '')).toLowerCase().indexOf(term) > -1;
  });
  // Quem a regra sugere e ainda não está na tarefa vem primeiro: é ali que
  // se age. Depois o que ainda pede decisão; os já matriculados por último.
  const ORDER = {};
  ORDER[REACH_MISSING] = 0;
  ORDER[REACH_IN] = 0;
  ORDER[REACH_UNKNOWN] = 1;
  ORDER[REACH_OUTSIDE] = 2;
  ORDER[REACH_ENROLLED] = 3;
  const rank = (row) => {
    if (row.status === REACH_UNKNOWN && row.enrolled) return ORDER[REACH_ENROLLED];
    return ORDER[row.status] === undefined ? 4 : ORDER[row.status];
  };
  filtered.sort((a, b) => rank(a) - rank(b));
  const shown = filtered.slice(0, limit);

  const profileText = (row) => {
    const profile = getCustomerProfile(row.customer);
    if (profile) {
      return profile.label;
    }
    const suggestion = row.status === REACH_UNKNOWN ? suggestionOf(row.customer) : null;
    if (suggestion && suggestion.key) {
      const suggested = getProfile(suggestion.key);
      return (
        <Tooltip title={'Pelo cadastro: ' + suggestion.reasons.join(' · ')}>
          <span>
            sugerida: {suggested ? suggested.label : suggestion.key}
            {suggestion.confidence === 'low' && <Tag style={{ marginLeft: 6, fontSize: 11 }}>palpite</Tag>}
          </span>
        </Tooltip>
      );
    }
    if (suggestion && suggestion.blocked === 'activity') {
      return 'sem CNAE: escolha a atividade';
    }
    return 'sem regra';
  };

  // ── o que falta fazer ────────────────────────────────────────────────────
  // A regra só sugere: cada pendência vira uma linha com o botão que a
  // resolve, para ninguém achar que o sistema já matriculou.
  const todos = [];
  if (reach) {
    if (reach.knowsEnrollment && reach.missing > 0) {
      todos.push({ key: REACH_MISSING, icon: 'user-add', cta: 'Revisar e matricular', count: reach.missing,
        text: say(reach.missing, 'cliente sugerido ainda não está na tarefa.', 'clientes sugeridos ainda não estão na tarefa.') });
    }
    if (!reach.knowsEnrollment && reach.in > 0) {
      todos.push({ key: 'in', icon: 'save', cta: 'Ver clientes', count: reach.in,
        text: say(reach.in, 'cliente sugerido', 'clientes sugeridos') + ': salve a tarefa e matricule aqui.' });
    }
    if (reach.unknown > 0) {
      todos.push({ key: REACH_UNKNOWN, icon: 'thunderbolt', cta: 'Definir regras', count: reach.unknown,
        text: say(reach.unknown, 'cliente sem regra', 'clientes sem regra') + ': sem ela o sistema não sabe se a tarefa serve.' });
    }
    if (reach.outside > 0) {
      todos.push({ key: REACH_OUTSIDE, icon: 'eye', cta: 'Revisar', count: reach.outside,
        text: say(reach.outside, 'cliente está na tarefa', 'clientes estão na tarefa') + ' sem a regra sugerir: exceção ou engano?' });
    }
  }
  const todo = reach && !noRegime ? (
    <div data-todo style={{ margin: '6px 0 0 34px' }}>
      {todos.length === 0 ? (
        <div data-todo-done style={{ padding: '12px 14px', borderRadius: 8, border: '1px solid #b7eb8f', background: '#f6ffed', fontSize: 14, color: '#237804' }}>
          <Icon type="check-circle" style={{ marginRight: 8 }} />
          Nada pendente: todos os clientes sugeridos já estão na tarefa.
        </div>
      ) : (
        <div style={{ borderRadius: 10, border: '1px solid #ffd591', background: '#fff7e6', overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px 4px', fontSize: 12, fontWeight: 700, letterSpacing: 0.4, color: '#ad4e00' }}>
            O QUE FALTA VOCÊ FAZER
          </div>
          {todos.map((item, index) => (
            <div key={item.key} data-todo-item={item.key} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
              borderTop: index > 0 ? '1px solid #ffe7ba' : 'none',
            }}>
              <strong style={{ minWidth: 36, fontSize: 22, lineHeight: 1, color: '#d46b08', textAlign: 'right' }}>{item.count}</strong>
              <span style={{ flex: 1, fontSize: 14, color: '#262626' }}>{item.text}</span>
              <Button type={filter === item.key ? 'default' : (index === 0 ? 'primary' : 'default')} icon={filter === item.key ? 'up' : item.icon}
                onClick={() => openFilter(item.key)} data-reach={item.key}>
                {filter === item.key ? 'Fechar' : item.cta}
              </Button>
            </div>
          ))}
        </div>
      )}
      {/* O que já está certo fica em segundo plano: uma linha, com link. */}
      {reach.knowsEnrollment && (
        <div data-summary style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
          <Icon type="check-circle" style={{ color: '#52c41a' }} />
          <button type="button" data-reach={REACH_ENROLLED} onClick={() => openFilter(REACH_ENROLLED)}
            style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: '#595959', textDecoration: 'underline dotted', fontFamily: 'inherit' }}>
            {say(reach.enrolled, '1 cliente já matriculado', reach.enrolled + ' clientes já matriculados')}
          </button>
        </div>
      )}
    </div>
  ) : null;

  // "Não possui regra": decisão consciente de que a tarefa é escolhida
  // cliente a cliente. Tira a tarefa das pendências de regra.
  const noneRow = onNone ? (
    <div data-no-rule style={{
      display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '10px 14px',
      borderRadius: 8, border: '1px solid ' + (none ? '#d3adf7' : '#f0f0f0'), background: none ? '#f9f0ff' : '#fafafa',
    }}>
      <Switch size="small" checked={!!none} onChange={onNone} data-no-rule-switch />
      <span style={{ flex: 1, fontSize: 13, color: '#262626' }}>
        Não possui regra
        <Tooltip title={NO_RULE_HELP} overlayStyle={{ maxWidth: 320 }}>
          <Icon type="question-circle" style={{ marginLeft: 6, color: '#8c8c8c' }} data-no-rule-help />
        </Tooltip>
      </span>
      {!none && <small style={{ color: '#8c8c8c' }}>Para tarefas escolhidas cliente a cliente</small>}
    </div>
  ) : null;

  // A tarefa é montada em duas colunas: `part="rule"` desenha só a regra
  // (definir), `part="result"` só a sugestão e os clientes (agir). Sem
  // `part`, desenha tudo numa coluna.
  const noneText = (
    <Sentence style={{ color: '#595959' }}>
      O sistema não sugere esta tarefa para nenhum cliente. Ela aparece na <strong>Etapa 2</strong> da aba Regra
      de cada cliente, para ser ligada à mão.
    </Sentence>
  );

  if (part === 'result') {
    return (
      <Flow>
        {none ? (
          <Block data-result>
            <BlockHead>
              <Step bg="#722ed1"><Icon type="team" /></Step>
              <Title>
                Sugestão para os clientes
                <small>Esta tarefa não possui regra</small>
              </Title>
            </BlockHead>
            {noneText}
          </Block>
        ) : (
      <Block data-result>
        <BlockHead>
          <Step bg={noRegime ? '#bfbfbf' : '#52c41a'}><Icon type="team" /></Step>
          <Title>
            Sugestão para os clientes
            <small>Nada é matriculado sozinho: confira e confirme cada ação abaixo</small>
          </Title>
          {reach && (
            <Button size="small" type="link" onClick={() => openFilter('all')} data-see-all>
              {filter === 'all' ? 'Fechar lista' : 'Ver todos'}
            </Button>
          )}
        </BlockHead>

        {noRegime ? (
          <Sentence style={{ color: '#8c8c8c' }}>
            Marque ao menos um regime — sem isso o sistema não sabe para quem sugerir a tarefa.
          </Sentence>
        ) : (
          <Sentence>{explainRule(onlyDomestic ? { regimes } : rule)}</Sentence>
        )}

        {reach && (
          <>
            {todo}
            {reach.in === 0 && (!filter || filter === 'all') && (
              <Footer>Nenhum cliente ativo se encaixa nesta regra hoje.</Footer>
            )}

            {filter && (
              <Panel ref={panelRef} data-reach-panel>
                <PanelHead>
                  <div style={{ flex: 1 }}>
                    <h4>{TITLES[filter]} · {filtered.length}</h4>
                    <p>{filterHelp[filter]}</p>
                  </div>
                  {bulkAction()}
                  <Button size="small" icon="close" type="link" style={{ color: '#8c8c8c' }} onClick={() => setFilter(null)} aria-label="Fechar lista" />
                </PanelHead>
                {(rows.length > PAGE || term) && (
                  <div style={{ padding: '8px 12px 0' }}>
                    <Input
                      size="small"
                      allowClear
                      prefix={<Icon type="search" style={{ color: '#bfbfbf' }} />}
                      placeholder="Buscar cliente"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </div>
                )}
                {shown.length === 0 ? (
                  <EmptyPanel>
                    <Icon type="check-circle" style={{ color: '#52c41a', marginRight: 6 }} />
                    {term ? 'Nenhum cliente com esse nome.' : (EMPTY[filter] || 'Nenhum cliente nesta situação.')}
                  </EmptyPanel>
                ) : (
                  shown.map((row) => {
                    const status = STATUS[row.status];
                    return (
                      <Row key={row.customer.id}>
                        <Who>
                          <div>
                            {row.customer.cod && <strong style={{ marginRight: 4 }}>{row.customer.cod} -</strong>}
                            {row.customer.name}
                          </div>
                          <small>{row.customer.type_taxation || 'sem regime'} · {profileText(row)}</small>
                        </Who>
                        <RowSide>
                          {ruleTag(row)}
                          {row.enrolled && <Tag color="blue" data-enrolled-tag>Matriculado</Tag>}
                          {actionFor(row)}
                        </RowSide>
                      </Row>
                    );
                  })
                )}
                {filtered.length > shown.length && (
                  <div style={{ padding: 8, textAlign: 'center', borderTop: '1px solid #f5f5f5' }}>
                    <Button size="small" type="link" onClick={() => setLimit(limit + PAGE * 2)}>
                      Mostrar mais ({filtered.length - shown.length})
                    </Button>
                  </div>
                )}
              </Panel>
            )}
          </>
        )}
      </Block>
        )}
      </Flow>
    );
  }

  if (none) {
    return (
      <Flow>
        {noneRow}
        {noneText}
      </Flow>
    );
  }

  return (
    <Flow>
      {noneRow}
      <ChoiceBlock
        axis="regimes"
        step="1"
        title="Regime tributário"
        help="Obrigatório. O cliente precisa ser de um dos marcados."
        options={REGIMES}
        value={regimes}
        onChange={onRegimes}
      />

      <Joiner note={onlyDomestic ? 'Doméstica não usa atividade nem folha' : null} />

      <ChoiceBlock
        axis="activities"
        step="2"
        title="Atividade"
        help="Deixe em Qualquer se a tarefa não depende disso."
        anyLabel="Qualquer"
        options={ACTIVITIES}
        value={activities}
        onChange={onActivities}
        disabled={cutsDisabled}
      />

      <Joiner />

      <ChoiceBlock
        axis="payroll"
        step="3"
        title="Folha de pagamento"
        help="Ex.: 13º, férias e FGTS só para quem tem folha."
        anyLabel="Tanto faz"
        options={PAYROLL}
        value={payroll}
        onChange={onPayroll}
        disabled={cutsDisabled}
      />
      {part !== 'rule' && (
        <>
      <Connector>
        <Joint style={{ borderColor: '#b7eb8f', color: '#389e0d' }}>
          <Icon type="arrow-down" />
        </Joint>
      </Connector>
      <Block data-result>
        <BlockHead>
          <Step bg={noRegime ? '#bfbfbf' : '#52c41a'}><Icon type="team" /></Step>
          <Title>
            Sugestão para os clientes
            <small>Nada é matriculado sozinho: confira e confirme cada ação abaixo</small>
          </Title>
          {reach && (
            <Button size="small" type="link" onClick={() => openFilter('all')} data-see-all>
              {filter === 'all' ? 'Fechar lista' : 'Ver todos'}
            </Button>
          )}
        </BlockHead>

        {noRegime ? (
          <Sentence style={{ color: '#8c8c8c' }}>
            Marque ao menos um regime — sem isso o sistema não sabe para quem sugerir a tarefa.
          </Sentence>
        ) : (
          <Sentence>{explainRule(onlyDomestic ? { regimes } : rule)}</Sentence>
        )}

        {reach && (
          <>
            {todo}
            {reach.in === 0 && (!filter || filter === 'all') && (
              <Footer>Nenhum cliente ativo se encaixa nesta regra hoje.</Footer>
            )}

            {filter && (
              <Panel ref={panelRef} data-reach-panel>
                <PanelHead>
                  <div style={{ flex: 1 }}>
                    <h4>{TITLES[filter]} · {filtered.length}</h4>
                    <p>{filterHelp[filter]}</p>
                  </div>
                  {bulkAction()}
                  <Button size="small" icon="close" type="link" style={{ color: '#8c8c8c' }} onClick={() => setFilter(null)} aria-label="Fechar lista" />
                </PanelHead>
                {(rows.length > PAGE || term) && (
                  <div style={{ padding: '8px 12px 0' }}>
                    <Input
                      size="small"
                      allowClear
                      prefix={<Icon type="search" style={{ color: '#bfbfbf' }} />}
                      placeholder="Buscar cliente"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </div>
                )}
                {shown.length === 0 ? (
                  <EmptyPanel>
                    <Icon type="check-circle" style={{ color: '#52c41a', marginRight: 6 }} />
                    {term ? 'Nenhum cliente com esse nome.' : (EMPTY[filter] || 'Nenhum cliente nesta situação.')}
                  </EmptyPanel>
                ) : (
                  shown.map((row) => {
                    const status = STATUS[row.status];
                    return (
                      <Row key={row.customer.id}>
                        <Who>
                          <div>
                            {row.customer.cod && <strong style={{ marginRight: 4 }}>{row.customer.cod} -</strong>}
                            {row.customer.name}
                          </div>
                          <small>{row.customer.type_taxation || 'sem regime'} · {profileText(row)}</small>
                        </Who>
                        <RowSide>
                          {ruleTag(row)}
                          {row.enrolled && <Tag color="blue" data-enrolled-tag>Matriculado</Tag>}
                          {actionFor(row)}
                        </RowSide>
                      </Row>
                    );
                  })
                )}
                {filtered.length > shown.length && (
                  <div style={{ padding: 8, textAlign: 'center', borderTop: '1px solid #f5f5f5' }}>
                    <Button size="small" type="link" onClick={() => setLimit(limit + PAGE * 2)}>
                      Mostrar mais ({filtered.length - shown.length})
                    </Button>
                  </div>
                )}
              </Panel>
            )}
          </>
        )}
      </Block>
        </>
      )}
    </Flow>
  );
};

export default EnrollmentRule;
